import preset from '../../../../contracts/native-sportsbook/v1/experience.json'
import { eventMatchesManifestScope, sportDisplayName, canonicalSportID, normalizedProviderID } from './nativePreviewModel'

export const EXPERIENCE_CAPABILITY = 'experience.composition.v1'
export const SECTION_NAMES = { event_header: 'Matchup header', event_probability: 'Market probabilities', event_insights: 'Match insights', event_markets: 'Markets' }
export const CATEGORIES = { all: 'All markets', popular: 'Main markets', spreads: 'Spreads & lines', totals: 'Totals', props: 'Game props', player_props: 'Player props', batter_props: 'Batter props', pitcher_props: 'Pitcher props', team_markets: 'Team markets', cards_and_corners: 'Cards & corners', periods: 'Periods', scoring: 'Scoring', same_game_parlay: 'Same Game Parlay' }
export const PHASES = [{ value: 'pre_match', label: 'Before start' }, { value: 'in_play', label: 'Live' }]
export const DEFAULT_LAYOUT = preset.event_templates[0].layout
const specificity = ['default', 'sport', 'league', 'event']

export function templateMatches(template, event) {
  if (!event) return template.scope.kind === 'default'
  const { kind, value } = template.scope
  if (kind === 'default') return true
  if (kind === 'event') return event.id === value
  return String(event[kind] || '').toUpperCase() === String(value || '').toUpperCase()
}

// Same sparse cascade as ExperienceConfiguration on both native platforms.
export function resolveEventPresentation(experience, event, phase = event?.live ? 'in_play' : 'pre_match') {
  const result = { ...structuredClone(DEFAULT_LAYOUT), sources: {}, templates: [], template_id: null, screen_id: null, scene_id: null }
  for (const template of [...(experience?.event_templates || [])].filter((candidate) => templateMatches(candidate, event)).sort((a, b) => specificity.indexOf(a.scope.kind) - specificity.indexOf(b.scope.kind))) {
    for (const [field, value] of Object.entries(template.layout)) { result[field] = value; result.sources[field] = template.name }
    result.templates.push(template.name)
    result.template_id = template.id
    result.screen_id = template.screen_id
    const scene = template.scenes?.[phase]
    if (scene) {
      for (const [field, value] of Object.entries(scene.layout)) { result[field] = value; result.sources[field] = `${template.name} · ${PHASES.find((item) => item.value === phase)?.label}` }
      result.scene_id = scene.id
    }
  }
  return result
}

export function resolveHomeScene(experience, events, now = new Date(), phase) {
  return experience?.home_scenes?.find((scene) => {
    const matches = events.filter((event) => scene.sport_ids.some((scope) => eventMatchesManifestScope(event, scope)))
    if (!matches.length) return false
    if (phase) return scene.state === phase
    const live = matches.some((event) => event.live)
    return scene.state === 'in_play' ? live : !live && matches.some((event) => !event.live && new Date(event.start_time) >= now)
  }) ?? null
}

export function applyHomeScene(scene, components) {
  if (!scene) return components
  return components.filter((component) => !scene.hidden_component_ids.includes(component.id)).toSorted((a, b) => {
    if (a.type === 'compliance_notice') return 1
    if (b.type === 'compliance_notice') return -1
    const rank = (component) => { const i = scene.component_order.indexOf(component.id); return i < 0 ? Infinity : i }
    return rank(a) - rank(b)
  })
}

export function createExperience(uuid = () => crypto.randomUUID()) {
  const ids = new Map()
  const remap = (value) => {
    if (Array.isArray(value)) return value.map(remap)
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, ['id', 'screen_id', 'slot_id'].includes(key) ? (ids.has(item) ? ids.get(item) : (ids.set(item, uuid()), ids.get(item))) : remap(item)]))
    return value
  }
  return remap({ id: preset.id, name: 'Sportsbook', event_templates: [{ ...preset.event_templates[0], name: 'Default event layout' }], home_scenes: [] })
}

export function resolveEditingPresentation(experience, template, event, layer) {
  const level = specificity.indexOf(template.scope.kind)
  const ancestors = experience.event_templates
    .filter((item) => specificity.indexOf(item.scope.kind) <= level)
    .map((item) => layer === 'base' ? { ...item, scenes: {} } : item)
  const example = event ?? { [template.scope.kind === 'event' ? 'id' : template.scope.kind]: template.scope.value }
  // Without a game, an event/competition ID does not identify its parent scopes.
  // Keep unresolved inheritance out of the canvas and editable section order.
  const inheritanceComplete = Boolean(event) || !ancestors.some((item) => item.scope.kind !== 'default' && specificity.indexOf(item.scope.kind) < level)
  return { ...resolveEventPresentation({ ...experience, event_templates: ancestors }, example, layer === 'base' ? 'pre_match' : layer), inheritanceComplete }
}

export function experienceTargetOptions(catalog, kind) {
  const entries = (catalog.events || []).flatMap((event) => {
    const value = kind === 'event' ? event.id : event[kind]
    return value ? [{ value, label: kind === 'event' ? event.name : kind === 'sport' ? sportDisplayName(value) : value }] : []
  })
  const unique = [...new Map(entries.map((entry) => [entry.value, entry])).values()]
  return unique.map((entry) => !(kind === 'sport' && canonicalSportID(entry.value) === normalizedProviderID(entry.value)) && unique.some((other) => other.value !== entry.value && other.label === entry.label) ? { ...entry, label: `${entry.label} (${entry.value})` } : entry).sort((a, b) => a.label.localeCompare(b.label))
}

export function earlierHomeVariations(experience, scene, catalog) {
  const index = experience.home_scenes.findIndex((item) => item.id === scene.id)
  const active = (item) => resolveHomeScene({ home_scenes: [item] }, catalog.events)
  return experience.home_scenes.slice(0, index).filter((item) => (active(scene) && active(item)) || (item.state === scene.state && (
    item.sport_ids.some((value) => scene.sport_ids.some((other) => other.toUpperCase() === value.toUpperCase())) ||
    catalog.events.some((event) => item.sport_ids.some((value) => eventMatchesManifestScope(event, value)) && scene.sport_ids.some((value) => eventMatchesManifestScope(event, value)))
  )))
}

export function updateTemplateLayout(experience, templateId, layer, field, value, uuid = () => crypto.randomUUID()) {
  const next = structuredClone(experience)
  const template = next.event_templates.find((item) => item.id === templateId)
  if (!template) return next
  if (layer !== 'base' && !template.scenes?.[layer] && value === undefined) return next
  if (layer !== 'base') {
    template.scenes ||= {}
    template.scenes[layer] ||= { id: uuid(), layout: {} }
  }
  const layout = layer === 'base' ? template.layout : template.scenes[layer].layout
  if (value === undefined) delete layout[field]
  else layout[field] = value
  return next
}

// Rehearsal changes sample presentation only; it never enters a signed document.

export function eventLayoutCopyIssue(experience, scope) {
  if (experience.event_templates.length >= 32) return 'This app has reached its game page limit.'
  if (!['sport', 'league', 'event'].includes(scope.kind) || !scope.value?.trim()) return 'Choose a sport, league or game.'
  const normalized = (value) => scope.kind === 'event' ? value : value?.toUpperCase()
  if (experience.event_templates.some((item) => item.scope.kind === scope.kind && normalized(item.scope.value) === normalized(scope.value.trim()))) return 'That already has a game page.'
  return null
}

export function duplicateEventLayout(experience, templateId, scope, name, uuid = () => crypto.randomUUID()) {
  const source = experience.event_templates.find((item) => item.id === templateId)
  if (!source) throw new Error('The source layout is no longer available.')
  return insertEventLayout(experience, source, scope, name, uuid)
}

export function insertEventLayout(experience, source, scope, name, uuid = () => crypto.randomUUID()) {
  const issue = eventLayoutCopyIssue(experience, scope)
  if (issue) throw new Error(issue)
  if (!name.trim()) throw new Error('Name the copied layout.')
  // A section may be repeated across Default/Before start/Live overrides. Map
  // each identity once so its new type/slot relationship remains consistent.
  const identities = new Map()
  const renew = (value) => {
    if (Array.isArray(value)) return value.map(renew)
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => {
      if (!['id', 'screen_id', 'slot_id'].includes(key)) return [key, renew(item)]
      if (!identities.has(item)) identities.set(item, uuid())
      return [key, identities.get(item)]
    }))
    return value
  }
  const template = { ...renew(source), name: name.trim().slice(0, 80), scope: { kind: scope.kind, value: scope.value.trim() } }
  return { experience: { ...experience, event_templates: [...experience.event_templates, template] }, template }
}

export function duplicateHomeVariation(experience, sceneId, uuid = () => crypto.randomUUID()) {
  const source = experience.home_scenes.find((item) => item.id === sceneId)
  if (!source) throw new Error('The source variation is no longer available.')
  return insertHomeVariation(experience, source, sceneId, uuid)
}

export function insertHomeVariation(experience, source, afterSceneId, uuid = () => crypto.randomUUID()) {
  if (experience.home_scenes.length >= 16) throw new Error('This Home has reached its variation limit.')
  const index = experience.home_scenes.findIndex((item) => item.id === afterSceneId)
  const names = new Set(experience.home_scenes.map((item) => item.name))
  let suffix = ' copy', number = 2
  while (names.has(source.name.slice(0, 80 - suffix.length) + suffix)) suffix = ` copy ${number++}`
  // These references deliberately point to the existing shared Home content.
  const scene = { ...structuredClone(source), id: uuid(), name: source.name.slice(0, 80 - suffix.length) + suffix }
  const scenes = [...experience.home_scenes]; scenes.splice(index < 0 ? scenes.length : index + 1, 0, scene)
  return { experience: { ...experience, home_scenes: scenes }, scene }
}
